<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="Service Tickets" backTo="internal">
      <button
          @click="showingCreateTicketPanel = true"
          class="flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path stroke="none" d="M0 0h24v24H0z"/>
          <path
              d="M15 5v2M15 11v2M15 17v2M5 5h14a2 2 0 012 2v3a2 2 0 000 4v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a2 2 0 000-4V7a2 2 0 012-2"
          />
        </svg>
        <span>Create Ticket</span>
      </button>
    </page-header>

    <div class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white p-5 rounded shadow-md relative">
        <div class="w-full flex items-center justify-between">
          <div class="flex -mx-0 mr-2">
            <TabHeaders :tabs="tabs" :selectedTab="currentTab" @tab-selected="onTabSelected"/>
          </div>
          <div class="flex flex-col flex-1">
            <div class="flex justify-end">
              <input
                  type="text"
                  class="ml-6 w-full lg:w-1/2 rounded-l bge-input bge-input-spacing"
                  placeholder="Search service tickets"
                  v-model="searchTextInput"
                  @keydown.enter="triggerSearch"
              />
              <button
                  @click="triggerSearch"
                  class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out"
              >
                <Icon iconType="search"/>
              </button>
              <TicketSearchTips class="ml-2" />
            </div>
          </div>
        </div>

        <TabContent :currentTab="currentTab">
          <template v-slot:[currentTab]>
            <DataTableComponent
                :key="currentTab"
                :columns="tableColumns"
                :rowClass="rowClass"
                @row-click="onRowClick"
            >
              <template v-slot:warranty-number-slot="{ rowData }">
                {{ rowData.warranty_number || "" }}
              </template>
              <template v-slot:return-progress-slot="{ rowData }">
                <span
                    v-if="rowData.return_progress"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide whitespace-no-wrap"
                    :class="returnProgressClass(rowData.return_progress.stage)"
                    :title="rowData.return_progress.return_no || ''"
                >{{ rowData.return_progress.label }}</span>
              </template>

              <template v-slot:created-at-slot="{ rowData }">
                {{ formatDateTime(rowData.created_at) }}
              </template>

              <template v-slot:severity-slot="{ rowData }">
                <span
                    class="px-2 rounded-lg"
                    :class="{
                      'bg-red-200 text-red-700': rowData.severity == 3,
                      'bg-yellow-200 text-yellow-700': rowData.severity == 2,
                      'bg-green-200 text-green-700': rowData.severity == 1,
                      'bg-gray-300 text-gray-700': rowData.severity == 0,
                    }"
                >{{ severityLabel(rowData.severity) }}</span>
              </template>

              <template v-slot:customer-slot="{ rowData }">
                {{ rowData.contact_name || "" }}
              </template>

              <template v-slot:equipment-slot="{ rowData }">
                {{ rowData.equipment_name || "" }}
              </template>

              <template v-slot:follow-up-slot="{ rowData }">
                {{ formatDate(rowData.follow_up_at) }}
              </template>
            </DataTableComponent>
          </template>
        </TabContent>
      </div>
    </div>

    <portal to="overlay-outlet">
      <Panel
          :showing="showingCreateTicketPanel"
          @close="showingCreateTicketPanel = false"
          title="Create Service Ticket"
      >
        <CreateServiceTicket @complete="ticketCreated"/>
      </Panel>
    </portal>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Panel from "@/components/Panel.vue";
import TabHeaders from "@/components/TabHeaders.vue";
import TabContent from "@/components/TabContent.vue";
import Icon from "@/components/Icon.vue";
import DataTableComponent from "@/views/ServiceTickets/Components/DataTableComponent.vue";
import CreateServiceTicket from "@/components/Dashboard/ServiceTickets/CreateServiceTicket.vue";
import TicketSearchTips from "@/components/ServiceTickets/TicketSearchTips.vue";
import {mapActions, mapState} from "vuex";

export default {
  name: "ServiceTickets",
  components: {
    PageHeader,
    Panel,
    TabHeaders,
    TabContent,
    Icon,
    DataTableComponent,
    CreateServiceTicket,
    TicketSearchTips,
  },
  data() {
    return {
      showingCreateTicketPanel: false,
      tabs: [
        {id: "new", label: "New Tickets", iconType: "pi-ticket"},
        {id: "review", label: "Under Review", iconType: "pi-ticket"},
        {id: "closed", label: "Closed Tickets", iconType: "pi-ticket"},
        {id: "all", label: "All Tickets", iconType: "pi-ticket"},
        {id: "warranty", label: "Warranty", iconType: "pi-shield"},
        {id: "items_to_collect", label: "Items to Collect", iconType: "pi-box"},
      ],
      return_progress_column: {
        field: "return_progress",
        header: "Status",
        sortable: false,
        custom: true,
        slotName: "return-progress-slot",
        width: "6%",
      },
      ticket_columns: [
        {
          field: "ticket_number",
          header: "Ticket #",
          sortable: true,
          custom: false,
          width: "10%",
        },
        {
          field: "warranty_number",
          header: "Warranty #",
          sortable: true,
          custom: true,
          slotName: "warranty-number-slot",
          width: "10%",
        },
        {
          field: "created_at",
          header: "Created",
          sortable: true,
          custom: true,
          slotName: "created-at-slot",
          width: "13%",
        },
        {
          field: "severity",
          header: "Severity",
          sortable: true,
          custom: true,
          slotName: "severity-slot",
          width: "9%",
        },
        {
          field: "title",
          header: "Title",
          sortable: true,
          custom: false,
          width: "20%",
        },
        {
          field: "contact_name",
          header: "Customer",
          sortable: true,
          custom: true,
          slotName: "customer-slot",
          width: "15%",
        },
        {
          field: "equipment_name",
          header: "Equipment",
          sortable: true,
          custom: true,
          slotName: "equipment-slot",
          width: "15%",
        },
        {
          field: "follow_up_at",
          header: "Follow Up At",
          sortable: true,
          custom: true,
          slotName: "follow-up-slot",
          width: "8%",
        },
      ],
    };
  },
  computed: {
    ...mapState("serviceTicket", ["currentTab", "lastSelectedTicket"]),
    tableColumns() {
      if (this.currentTab !== "items_to_collect") return this.ticket_columns;
      const narrower = {title: "17%", contact_name: "13%", equipment_name: "13%"};
      const columns = this.ticket_columns.map((col) =>
          narrower[col.field] ? {...col, width: narrower[col.field]} : col
      );
      return [...columns, this.return_progress_column];
    },

    searchTextInput: {
      get() {
        const tab = this.$store.state.serviceTicket.tabs[this.currentTab];
        return tab ? tab.searchText : "";
      },
      set(value) {
        this.updateSearchText(value);
      },
    },
  },
  mounted() {
    this.fetchTableData();
  },
  methods: {
    ...mapActions("serviceTicket", [
      "fetchTableData",
      "updateTab",
      "updateSearchText",
      "search",
      "setLastSelectedTicket",
    ]),

    onTabSelected(newTab) {
      this.updateTab(newTab);
    },
    triggerSearch() {
      this.search(this.searchTextInput);
    },
    onRowClick(event) {
      this.setLastSelectedTicket(event.data.ticket_id);
      this.$router.push({
        name: "service-ticket-edit",
        params: {ticketId: event.data.ticket_id},
      });
    },
    ticketCreated() {
      this.showingCreateTicketPanel = false;
      this.fetchTableData();
    },
    severityLabel(severity) {
      switch (parseInt(severity)) {
        case 1:
          return "Low";
        case 2:
          return "Medium";
        case 3:
          return "High";
        default:
          return "Normal";
      }
    },
    returnProgressClass(stage) {
      switch (stage) {
        case "received":
          return "bg-green-100 text-green-700 border border-green-200";
        case "label_sent":
          return "bg-blue-100 text-blue-700 border border-blue-200";
        case "return_created":
        case "form_sent":
          return "bg-yellow-100 text-yellow-700 border border-yellow-200";
        default:
          return "bg-gray-100 text-gray-700 border border-gray-200";
      }
    },
    formatDate(unixTimestamp) {
      if (!unixTimestamp) {
        return "";
      }
      return this.$moment.unix(unixTimestamp).format("DD/MM/YYYY");
    },
    formatDateTime(unixTimestamp) {
      if (!unixTimestamp) {
        return "";
      }
      return this.$moment.unix(unixTimestamp).format("DD/MM/YYYY HH:mm");
    },
    rowClass(rowData) {
      const classes = [];

      if (rowData.is_closed) {
        classes.push("row-closed-ticket");
      }

      if (rowData.ticket_id === this.lastSelectedTicket) {
        classes.push("highlight-row");
      }

      return classes.join(" ");
    },
  },
};
</script>

<style scoped>

::v-deep .row-closed-ticket {
  color: red !important;
}

::v-deep .highlight-row {
  background-color: rgba(0, 0, 0, .15) !important;
}

</style>
